import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { submitPartnerInquiry } from '../lib/supabase';

// Icons
const XCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-red-500 flex-shrink-0">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
);

const LoadingSpinner = () => (
  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-brand-success flex-shrink-0">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

// Navigation (simplified for partner page)
const PartnerNavigation: React.FC = () => {
  return (
    <nav className="fixed top-0 left-0 right-0 z-[1000] bg-[#1a365d]/95 backdrop-blur-sm border-b border-white/10 py-3.5">
      <div className="max-w-[1100px] mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center no-underline">
          <span className="font-display text-2xl font-bold text-white">
            Aid<span className="text-brand-accent">Fone</span>
          </span>
        </Link>
        <a
          href="#contact"
          className="inline-flex items-center justify-center px-5 py-2.5 font-body text-sm font-semibold no-underline rounded-md border-none cursor-pointer transition-all bg-brand-accent text-white shadow-[0_4px_14px_rgba(224,123,76,0.35)] hover:bg-[#c96a3f] hover:-translate-y-0.5"
        >
          Contact Us
        </a>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection: React.FC = () => {
  return (
    <section className="pt-28 pb-20 bg-gradient-to-br from-[#1a365d] via-[#1e4a7a] to-[#16213e] text-white">
      <div className="max-w-[900px] mx-auto px-6 text-center">
        <h1 className="font-display text-[clamp(2rem,5vw,3rem)] font-bold mb-6 leading-tight">
          Building health tech for vulnerable populations?
        </h1>
        <p className="text-[clamp(1.25rem,3vw,1.75rem)] text-white/80 mb-8 font-display font-medium">
          Your AI can detect. But can your users respond?
        </p>
        <p className="text-lg text-white/70 leading-relaxed max-w-[700px] mx-auto">
          14 million North Americans are digitally excluded — seniors with dementia,
          physical limitations, or cognitive decline who can't use apps, ignore notifications,
          or simply don't understand technology. Your alerts reach devices, not people.
        </p>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection: React.FC = () => {
  const problems = [
    "AI detects a fall — the user can't navigate the app to respond",
    "Medication reminder fires — gets dismissed and forgotten instantly",
    "Anomaly detected in vitals — notification sits unread for hours",
    "Emergency alert sent — user doesn't understand what it means",
    "Family gets pinged — but has no way to verify or intervene remotely",
  ];

  return (
    <section className="py-20 bg-brand-bg-warm">
      <div className="max-w-[900px] mx-auto px-6">
        <h2 className="font-display text-[clamp(1.6rem,4vw,2.2rem)] font-semibold text-brand-text text-center mb-4">
          The Last-Mile Problem
        </h2>
        <p className="text-center text-brand-text-light text-lg mb-12 max-w-[600px] mx-auto">
          Your technology works. But for millions of users, a notification
          might as well not exist.
        </p>

        <div className="space-y-4">
          {problems.map((problem, idx) => (
            <div
              key={idx}
              className="flex items-start gap-4 bg-white rounded-xl p-5 border border-brand-border shadow-sm"
            >
              <XCircleIcon />
              <p className="text-brand-text text-base leading-relaxed m-0">{problem}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solution Section Icons
const ApiIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
  </svg>
);

const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
  </svg>
);

const FamilyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
  </svg>
);

const HandshakeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
  </svg>
);

// Solution Section
const SolutionSection: React.FC = () => {
  const steps = [
    {
      title: "Your API",
      description: "Your fall detection, medication system, or health monitoring sends an alert",
      icon: <ApiIcon />,
    },
    {
      title: "AidFone Portal",
      description: "Translates tech alerts into accessible, simplified phone notifications",
      icon: <PhoneIcon />,
    },
    {
      title: "Family Notification",
      description: "Caregivers receive context-rich alerts with one-tap response options",
      icon: <FamilyIcon />,
    },
    {
      title: "Human Intervention",
      description: "Real people verify, respond, and take action when technology alone fails",
      icon: <HandshakeIcon />,
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1000px] mx-auto px-6">
        <h2 className="font-display text-[clamp(1.6rem,4vw,2.2rem)] font-semibold text-brand-text text-center mb-4">
          AidFone: The Accessibility Layer
        </h2>
        <p className="text-center text-brand-text-light text-lg mb-12 max-w-[700px] mx-auto">
          We bridge the gap between your sophisticated technology and users who need
          human-centered delivery.
        </p>

        {/* Flow Diagram */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-2">
          {steps.map((step, idx) => (
            <div key={idx} className="relative">
              <div className="bg-brand-bg rounded-xl p-6 border border-brand-border h-full flex flex-col items-center text-center">
                <div className="text-brand-primary mb-4">{step.icon}</div>
                <h3 className="font-display text-lg font-semibold text-brand-text mb-2">
                  {step.title}
                </h3>
                <p className="text-brand-text-light text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
              {/* Arrow connector (hidden on mobile and last item) */}
              {idx < steps.length - 1 && (
                <div className="hidden md:flex absolute top-1/2 -right-3 transform -translate-y-1/2 text-brand-primary z-10">
                  <ArrowRightIcon />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Market Section
const MarketSection: React.FC = () => {
  const stats = [
    {
      label: "Total Addressable Market",
      value: "14M",
      description: "North Americans digitally excluded",
      region: "USA + Canada",
    },
    {
      label: "Serviceable Market",
      value: "1.52M",
      description: "Canadians with accessibility needs",
      region: "Canada",
    },
    {
      label: "Initial Focus",
      value: "350K",
      description: "Quebec seniors needing assistance",
      region: "Quebec",
    },
  ];

  return (
    <section className="py-20 bg-brand-bg-warm">
      <div className="max-w-[1000px] mx-auto px-6">
        <h2 className="font-display text-[clamp(1.6rem,4vw,2.2rem)] font-semibold text-brand-text text-center mb-4">
          The Locked Market
        </h2>
        <p className="text-center text-brand-text-light text-lg mb-12 max-w-[700px] mx-auto">
          These users aren't reached by traditional digital health solutions.
          They're unreachable — until now.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl p-8 border border-brand-border shadow-sm text-center"
            >
              <div className="text-xs font-bold text-brand-accent uppercase tracking-wider mb-2">
                {stat.region}
              </div>
              <div className="font-display text-[clamp(2.5rem,6vw,3.5rem)] font-bold text-brand-primary mb-2">
                {stat.value}
              </div>
              <h3 className="font-semibold text-brand-text mb-1">{stat.label}</h3>
              <p className="text-brand-text-light text-sm">{stat.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section with Contact Form
const CTASection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      await submitPartnerInquiry({
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone || undefined,
        message: formData.message || undefined,
      });
      setStatus('success');
      setFormData({ name: '', company: '', email: '', phone: '', message: '' });
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
      console.error('Partner inquiry error:', error);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 bg-gradient-to-br from-[#1a365d] via-[#1e4a7a] to-[#16213e] text-white"
    >
      <div className="max-w-[600px] mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="font-display text-[clamp(1.6rem,4vw,2.2rem)] font-semibold mb-4">
            Partner With Us
          </h2>
          <p className="text-white/80 text-lg">
            Let's discuss how AidFone can extend your reach to digitally excluded populations.
          </p>
        </div>

        {status === 'success' ? (
          <div className="bg-brand-success/20 border-2 border-brand-success rounded-xl p-8 text-center">
            <div className="flex justify-center mb-4">
              <CheckIcon />
            </div>
            <h3 className="font-display text-xl font-semibold text-[#a7f3d0] mb-2">
              Thank you for reaching out!
            </h3>
            <p className="text-white/80">
              We'll review your inquiry and get back to you within 2 business days.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-white/90 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={status === 'loading'}
                  className="w-full px-4 py-3.5 border-2 border-white/20 rounded-lg bg-white/10 text-white text-base font-body outline-none transition-colors placeholder:text-white/50 focus:border-brand-accent disabled:opacity-50"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white/90 mb-2">
                  Company *
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  disabled={status === 'loading'}
                  className="w-full px-4 py-3.5 border-2 border-white/20 rounded-lg bg-white/10 text-white text-base font-body outline-none transition-colors placeholder:text-white/50 focus:border-brand-accent disabled:opacity-50"
                  placeholder="Acme Health Tech"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-white/90 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={status === 'loading'}
                  className="w-full px-4 py-3.5 border-2 border-white/20 rounded-lg bg-white/10 text-white text-base font-body outline-none transition-colors placeholder:text-white/50 focus:border-brand-accent disabled:opacity-50"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white/90 mb-2">
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={status === 'loading'}
                  className="w-full px-4 py-3.5 border-2 border-white/20 rounded-lg bg-white/10 text-white text-base font-body outline-none transition-colors placeholder:text-white/50 focus:border-brand-accent disabled:opacity-50"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/90 mb-2">
                Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                disabled={status === 'loading'}
                className="w-full px-4 py-3.5 border-2 border-white/20 rounded-lg bg-white/10 text-white text-base font-body outline-none transition-colors placeholder:text-white/50 focus:border-brand-accent disabled:opacity-50 resize-none"
                placeholder="Tell us about your product and how we might work together..."
              />
            </div>

            {status === 'error' && (
              <p className="text-red-400 text-sm">{errorMessage}</p>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full px-6 py-4 bg-brand-accent text-white border-none rounded-lg text-base font-semibold font-body cursor-pointer transition-all whitespace-nowrap hover:bg-[#c96a3f] hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
            >
              {status === 'loading' ? (
                <>
                  <LoadingSpinner />
                  <span>Sending...</span>
                </>
              ) : (
                'Send Inquiry'
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

// Footer
const PartnerFooter: React.FC = () => {
  return (
    <footer className="py-8 bg-[#0f1a2e] text-white/60">
      <div className="max-w-[1100px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <Link to="/" className="text-white/80 hover:text-white transition-colors text-sm">
          &larr; Back to AidFone.com
        </Link>
        <p className="text-sm">
          &copy; {new Date().getFullYear()} AidFone Inc. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// Main Partners Page
export const PartnersPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-brand-bg font-body">
      <PartnerNavigation />
      <main>
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <MarketSection />
        <CTASection />
      </main>
      <PartnerFooter />
    </div>
  );
};

export default PartnersPage;
